/**
 * CSV read/write for the test-questions import. RFC 4180 (quoted cells may
 * hold commas, quotes and newlines — question HTML does). Decoding mirrors
 * the backend's Utils.decodeCsvBuffer: UTF-8 first, Excel's
 * "CSV (Comma delimited)" Arabic codepage (windows-1256) as the fallback.
 */

export type QuestionCsv = {
	fields: string[];
	rows: Record<string, string>[];
};

export const QUESTION_CSV_COLUMNS = [
	'content',
	'score',
	'type',
	'model',
	'answer_mode',
	'image',
	'feedback',
	'ch1',
	'correct1',
	'ch2',
	'correct2',
	'ch3',
	'correct3',
	'ch4',
	'correct4',
];

export const decodeCsvBytes = (buffer: ArrayBuffer): string => {
	let text: string;
	try {
		text = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
	} catch {
		text = new TextDecoder('windows-1256').decode(buffer);
	}
	return text.replace(/^﻿/, '');
};

export const parseCsv = (text: string): string[][] => {
	const rows: string[][] = [];
	let row: string[] = [];
	let cell = '';
	let quoted = false;
	for (let i = 0; i < text.length; i += 1) {
		const char = text[i];
		if (quoted) {
			if (char === '"') {
				if (text[i + 1] === '"') {
					cell += '"';
					i += 1;
				} else quoted = false;
			} else cell += char;
		} else if (char === '"') quoted = true;
		else if (char === ',') {
			row.push(cell);
			cell = '';
		} else if (char === '\n' || char === '\r') {
			if (char === '\r' && text[i + 1] === '\n') i += 1;
			row.push(cell);
			rows.push(row);
			row = [];
			cell = '';
		} else cell += char;
	}
	if (cell !== '' || row.length) {
		row.push(cell);
		rows.push(row);
	}
	// Excel leaves fully blank lines between and after rows.
	return rows.filter((r) => r.some((value) => value.trim() !== ''));
};

const quoteCell = (value: string) =>
	/[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

export const serializeCsv = (rows: string[][]): string =>
	rows.map((row) => row.map((value) => quoteCell(value ?? '')).join(',')).join('\r\n');

export const parseQuestionCsv = async (file: File): Promise<QuestionCsv> => {
	const [header = [], ...body] = parseCsv(decodeCsvBytes(await file.arrayBuffer()));
	const fields = header.map((name) => name.trim());
	const rows = body.map((cells) => {
		const row: Record<string, string> = {};
		fields.forEach((name, index) => {
			row[name] = cells[index] ?? '';
		});
		return row;
	});
	return { fields, rows };
};

/** Re-encodes as UTF-8 with a BOM, keeping the teacher's column order and file name. */
export const buildQuestionCsvFile = (csv: QuestionCsv, name: string): File => {
	const text = serializeCsv([
		csv.fields,
		...csv.rows.map((row) => csv.fields.map((field) => row[field] ?? '')),
	]);
	return new File(['﻿' + text], name, { type: 'text/csv' });
};

/** The v2 template: one row per supported shape. */
export const downloadQuestionTemplate = () => {
	const example = (values: Record<string, string>) =>
		QUESTION_CSV_COLUMNS.map((column) => values[column] ?? '');
	const text = serializeCsv([
		QUESTION_CSV_COLUMNS,
		example({
			content: 'What is the capital of Egypt?',
			score: '1',
			type: 'mcq',
			feedback: 'Cairo has been the capital since 969.',
			ch1: 'Cairo',
			correct1: 'true',
			ch2: 'Alexandria',
			correct2: 'false',
			ch3: 'Giza',
			correct3: 'false',
		}),
		example({
			content: 'Describe what you see in the picture.',
			score: '5',
			type: 'essay',
			answer_mode: 'text',
			image: 'https://example.com/picture.png',
		}),
		example({
			content: 'Solve the problem in the picture and attach your working.',
			score: '10',
			type: 'essay',
			answer_mode: 'both',
			image: 'q1.png',
			feedback: 'Show every step.',
		}),
	]);
	const blob = new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8' });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.setAttribute('href', url);
	anchor.setAttribute('download', 'questions-import-template.csv');
	document.body.appendChild(anchor); // required for firefox
	anchor.click();
	anchor.remove();
	URL.revokeObjectURL(url);
};
